import React from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import './LinearProgressBar.css';

/**
 * A mapping of possible types (kinds) for the LinearProgressBar component.
 * Each kind corresponds to a specific visual styling of the progress bar.
 *
 * @property {string} warning  - Represents a warning state.
 * @property {string} success  - Represents a success state.
 * @property {string} critical - Represents a critical state.
 * @property {string} default  - Represents the default state.
 */
const KINDS = {
  warning: 'warning',
  success: 'success',
  critical: 'critical',
  default: 'default',
};

/**
 * LinearProgressBar is a functional React component that renders a progress bar
 * with optional styling and behavior based on its properties.
 *
 * @param {Object} props                     - The props passed to the component.
 * @param {number} props.value               - The current progress value (percentage). Ranges from 0 to 100.
 * @param {'warning'|'success'|'critical'|'default'} props.kind
 *                                           - The kind of progress bar to display (styling is determined by `KINDS`).
 * @param {boolean} props.showPointer        - Whether to display a pointer when progress is incomplete (value < 100).
 *
 * @returns {JSX.Element}                    - The rendered LinearProgressBar component.
 *
 * @example
 * <LinearProgressBar value={50} kind="success" showPointer={true} />
 * // Renders a progress bar with 50% completion, styled as a success, with a pointer.
 *
 * @example
 * <LinearProgressBar value={80} kind="critical" />
 * // Renders a progress bar with 80% completion, styled as critical, without a pointer.
 */
function LinearProgressBar({ value, kind, showPointer }) {
  const progressBarFillerColor = classnames(
    `LinearProgressBar-filler--${kind}`,
    showPointer && value < 100
      ? 'LinearProgressBar-filler--showPointer'
      : 'LinearProgressBar-filler--noPointer'
  );

  return (
    <div className='LinearProgressBar' data-testid='linear-progress-bar'>
      <div
        className={progressBarFillerColor}
        style={{ height: '100%', width: `${value}%` }}
      />
    </div>
  );
}

LinearProgressBar.propTypes = {
  value: PropTypes.number,
  kind: PropTypes.oneOf(Object.values(KINDS)),
  showPointer: PropTypes.bool,
};

LinearProgressBar.defaultProps = {
  value: 0,
  kind: KINDS.default,
  showPointer: false,
};

export default LinearProgressBar;
